import React from 'react';
import { useLoaderData, useParams } from '@tanstack/react-router';
import {
  isExpiredToken, isNotFoundError, isBadRequestError,
  isUnauthorizedError,
} from '../../utils/errors';
import ErrorPage from '../status-pages/error-page';
import { TokenExpirationMessage } from '../token-expiration-message/token-expiration-message';
import { displayCustomPage } from '../../utils/custom-page/display-custom-page';
import { getSurveyModel } from '../survey-questions/get-survey-model';
import { AdditionalThirdPartiesLoaderResponse } from '../../routes/$accountSlug/$submissionId/additional-third-parties/$type';
import Survey from '../survey/survey';

const ADDITIONAL_THIRD_PARTIES_PAGE_NAME = 'additionalThirdParties';

const AdditionalThirdPartiesWithToken: React.FC = () => {
  const {
    settings,
    error,
    type,
  } = useLoaderData({ strict: false }) as AdditionalThirdPartiesLoaderResponse;

  const {
    accountSlug,
    submissionId,
    token,
  } = useParams({ strict: false }) as { accountSlug: string; submissionId: string; token: string };

  // If there's no error, the loader will have redirected to the clean route
  // So we only need to handle error cases here
  if (isNotFoundError(error) || isBadRequestError(error)) {
    return <ErrorPage
      errorType={error}
      link={`/${accountSlug}/`}
      thirdPartyType={type}
           />;
  }

  // Handle token expiration - user can request a new token
  if (isExpiredToken(error)) {
    const surveyModel = getSurveyModel({
      settings,
      completedHtml: '',
    });

    displayCustomPage({
      survey: surveyModel,
      component: TokenExpirationMessage,
      getProps: () => ({
        publicId: submissionId,
        slug: accountSlug,
        currentToken: token,
        thirdPartyType: type,
      }),
      withoutFrame: true,
    });

    // Remove current page to display only token expiration message
    const additionalThirdPartiesPage = surveyModel.getPageByName(ADDITIONAL_THIRD_PARTIES_PAGE_NAME);
    if (additionalThirdPartiesPage) {
      surveyModel.removePage(additionalThirdPartiesPage);
    }
    surveyModel.showNavigationButtons = false;
    surveyModel.showProgressBar = 'off';
    surveyModel.showTOC = false;

    return (
      <Survey surveyModel={surveyModel} />
    );
  }

  // Handle invalid/wrong token - show error page
  if (isUnauthorizedError(error)) {
    return <ErrorPage
      errorType={error}
      link={`/${accountSlug}/`}
      thirdPartyType={type}
           />;
  }

  // If we reach here, there's no error and redirect should have happened
  // Return null as a fallback (shouldn't happen)
  return null;
};

export default AdditionalThirdPartiesWithToken;
